import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import dayjs from 'dayjs';

import {
  Modal,
  Box,
  Typography,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Tabs,
  Tab,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Link,
  TextField,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import {
  TemplateActions,
} from '../../Services/_actions/Template/template.actions.js';
import APIService from '../../Services/APIService';

import {
  LeadFormActions,
} from '../../Services/_actions/LeadForm/leadForm.actions.js';

import {
  dateFormate,
  indianTimeZone,
} from '../../utils/helper.jsx';

import CommonTable from '../Shared/Tables/TableV1.tsx';

/* =========================================================
   PROPS
========================================================= */

interface AllUpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
  leadFormId: string;
}

/* =========================================================
   TYPES
========================================================= */

interface StatusObj {
  _id?: string;
  created_by?: string;
  role?: string;

  status_id?: string;
  status_name?: string;
  name?: string;

  template_name?:
  | string
  | string[]
  | Record<string, any>
  | null;

  template_id?: string | null;

  template?:
  | {
    name?: string | null;
    id?: string | null;
    template_id?: string | null;
  }[]
  | Record<string, any>
  | null;

  updated_at?: string;
  created_at?: string;

  comment?: string;

  payload?: Record<string, any>;

  type_of_status?: string;
  type?: string;

  sub_status?: string;
  substatus?: string;
  sub_status_name?: string;

  [key: string]: any;
}

interface SelectedTemplate {
  name: string;
  id: string;
  data: any;
  sentDate?: string;
  sourceStatus?: StatusObj;
}

interface DateSummary {
  date?: string;
  sent_date?: string;
  total_users?: number;
  total?: number;
  delivered?: number;
  read?: number;
  clicked?: number;
  failed?: number;
  [key: string]: any;
}

/* =========================================================
   HELPERS
========================================================= */

const formatDate = (
  value?: string | null
) => {
  if (!value) return '-';

  try {
    return dateFormate(
      indianTimeZone(value),
      'DD-MM-YYYY hh:mm A'
    );
  } catch {
    return value;
  }
};

const formatSummaryDate = (value?: string | null) => {
  if (!value) return '-';

  try {
    return dateFormate(
      indianTimeZone(value),
      'DD-MM-YYYY'
    );
  } catch {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : String(value).split('T')[0];
  }
};

const toTime = (
  value?: string | Date | null
): number | null => {
  if (!value) return null;

  const time =
    new Date(value).getTime();

  return Number.isNaN(time)
    ? null
    : time;
};

const formatDuration = (
  seconds?: number | null
) => {
  if (
    seconds === undefined ||
    seconds === null ||
    Number.isNaN(seconds)
  ) {
    return '-';
  }

  const total = Math.max(
    0,
    Math.round(seconds)
  );

  const hours = Math.floor(
    total / 3600
  );

  const minutes = Math.floor(
    (total % 3600) / 60
  );

  const secs = total % 60;

  if (hours > 0) {
    return `${String(hours).padStart(
      2,
      '0'
    )}:${String(minutes).padStart(
      2,
      '0'
    )}:${String(secs).padStart(
      2,
      '0'
    )}`;
  }

  return `${String(minutes).padStart(
    2,
    '0'
  )}:${String(secs).padStart(
    2,
    '0'
  )}`;
};

const getResponse = (
  res: any
) =>
  res?.data ?? res;

const normalize = (
  value: any
) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const getFirstValue = (
  obj: any,
  keys: string[]
) => {
  for (const key of keys) {
    const value =
      obj?.[key];

    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      return value;
    }
  }

  return '';
};

/* =========================================================
   TEMPLATE NAME
========================================================= */

const getTemplateNames = (
  value: any
): string[] => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return [];
  }

  if (
    typeof value === 'string'
  ) {
    const name =
      value.trim();

    return name
      ? [name]
      : [];
  }

  if (
    Array.isArray(value)
  ) {
    const result: string[] = [];

    value.forEach(
      (item) => {
        if (
          item === null ||
          item === undefined
        ) {
          return;
        }

        if (
          typeof item === 'string'
        ) {
          const name =
            item.trim();

          if (name) {
            result.push(name);
          }

          return;
        }

        if (
          typeof item === 'object'
        ) {
          const obj =
            item as Record<
              string,
              any
            >;

          const name =
            obj.template_name ??
            obj.name ??
            obj.templateName ??
            obj.template;

          if (
            typeof name ===
            'string' &&
            name.trim()
          ) {
            result.push(
              name.trim()
            );
          }
        }
      }
    );

    return Array.from(
      new Set(result)
    );
  }

  if (
    typeof value === 'object'
  ) {
    const obj =
      value as Record<
        string,
        any
      >;

    const name =
      obj.template_name ??
      obj.name ??
      obj.templateName ??
      obj.template;

    if (
      typeof name ===
      'string' &&
      name.trim()
    ) {
      return [
        name.trim(),
      ];
    }
  }

  return [];
};

/* =========================================================
   TEMPLATE NAMES FROM STATUS ROW
========================================================= */

const getTemplateNamesFromRow = (
  row: StatusObj
): string[] => {
  const result: string[] = [];

  result.push(
    ...getTemplateNames(
      row?.template_name
    )
  );

  const template =
    row?.template;

  if (
    Array.isArray(template)
  ) {
    template.forEach(
      (item) => {
        if (
          item?.name
        ) {
          result.push(
            String(
              item.name
            ).trim()
          );
        }
      }
    );
  } else if (
    template &&
    typeof template ===
    'object'
  ) {
    const name =
      template?.name ||
      template?.template_name;

    if (name) {
      result.push(
        String(name).trim()
      );
    }
  }

  return Array.from(
    new Set(
      result.filter(Boolean)
    )
  );
};

/* =========================================================
   TEMPLATE ID FROM ROW
========================================================= */

const getTemplateIdFromRow = (
  row: StatusObj,
  templateName?: string
): string => {
  const directId =
    row?.template_id ||
    row?.templateId;

  if (
    directId !== undefined &&
    directId !== null &&
    directId !== ''
  ) {
    return String(
      directId
    );
  }

  const template =
    row?.template;

  if (
    Array.isArray(template)
  ) {
    const matched =
      template.find(
        (item) => {
          const itemName =
            item?.name ||
            item?.template_name ||
            '';

          if (
            templateName
          ) {
            return (
              normalize(
                itemName
              ) ===
              normalize(
                templateName
              )
            );
          }

          return Boolean(
            item?.id ||
            item?.template_id
          );
        }
      );

    if (
      matched?.id
    ) {
      return String(
        matched.id
      );
    }

    if (
      matched?.template_id
    ) {
      return String(
        matched.template_id
      );
    }

    const firstWithId =
      template.find(
        (item) =>
          item?.id ||
          item?.template_id
      );

    if (
      firstWithId?.id
    ) {
      return String(
        firstWithId.id
      );
    }

    if (
      firstWithId?.template_id
    ) {
      return String(
        firstWithId.template_id
      );
    }
  }

  if (
    template &&
    typeof template ===
    'object'
  ) {
    if (
      template?.id
    ) {
      return String(
        template.id
      );
    }

    if (
      template?.template_id
    ) {
      return String(
        template.template_id
      );
    }
  }

  const fallbackId =
    row?.md_id ||
    row?.template?.md_id;

  if (
    fallbackId
  ) {
    return String(
      fallbackId
    );
  }

  return '';
};

/* =========================================================
   STATUS HELPERS
========================================================= */

const getStatusLabel = (
  row: StatusObj
) =>
  row?.status_name ||
  row?.name ||
  '-';

const getStatusColor = (
  status = ''
): any => {
  const value =
    normalize(status);

  if (
    value.includes('resolved') ||
    value.includes('connected') ||
    value.includes('delivered')
  ) {
    return 'success';
  }

  if (
    value.includes('failed') ||
    value.includes('not connected') ||
    value.includes('bounce')
  ) {
    return 'error';
  }

  if (
    value.includes('read') ||
    value.includes('click') ||
    value.includes('open')
  ) {
    return 'info';
  }

  if (
    value.includes('later') ||
    value.includes('progress')
  ) {
    return 'warning';
  }

  return 'default';
};

const getTypeLabel = (
  row: StatusObj
) => {
  const type =
    normalize(
      row?.type_of_status
    );

  if (
    type === 'u' ||
    type === 'user'
  ) {
    return 'User';
  }

  if (
    type === 'a' ||
    type === 'agent'
  ) {
    return 'Agent';
  }

  if (
    type === 's' ||
    type === 'system'
  ) {
    return 'System';
  }

  return (
    row?.role ||
    row?.type ||
    'System'
  );
};

/* =========================================================
   EVENTS
========================================================= */

const isUserEvent = (
  row: StatusObj
) => {
  const status =
    normalize(
      getStatusLabel(row)
    );

  return (
    status.includes('read') ||
    status.includes('button clicked') ||
    status.includes('cta click') ||
    status === 'click' ||
    status.includes('chat')
  );
};

const isDeliveredEvent = (
  row: StatusObj
) =>
  normalize(
    getStatusLabel(row)
  ).includes('delivered');

const isReadEvent = (
  row: StatusObj
) =>
  normalize(
    getStatusLabel(row)
  ).includes('read');

const isCtaEvent = (
  row: StatusObj
) => {
  const status =
    normalize(
      getStatusLabel(row)
    );

  return (
    (
      status.includes('cta') &&
      (
        status.includes('click') ||
        status.includes('clicked')
      )
    ) ||
    status.includes('button clicked')
  );
};

const isChatEvent = (
  row: StatusObj
) =>
  normalize(
    getStatusLabel(row)
  ).includes('chat');

const isAgentResponseEvent = (
  row: StatusObj
) => {
  const status =
    normalize(
      getStatusLabel(row)
    );

  return (
    status.includes('cta open') ||
    status.includes('call connected') ||
    status.includes('connected') ||
    status.includes('call not connected') ||
    status.includes('not connected') ||
    status.includes('call later') ||
    status.includes('call scheduled') ||
    status.includes('intervene') ||
    status.includes('resolved') ||
    status.includes('feedback')
  );
};

const getLatestBefore = (
  rows: StatusObj[],
  currentIndex: number,
  predicate: (
    row: StatusObj
  ) => boolean
) => {
  for (
    let i =
      currentIndex - 1;
    i >= 0;
    i--
  ) {
    if (
      predicate(
        rows[i]
      )
    ) {
      return rows[i];
    }
  }

  return undefined;
};

const getResponseInfo = (
  rows: StatusObj[],
  index: number
) => {
  const current =
    rows[index];

  const currentTime =
    toTime(
      current?.updated_at ||
      current?.created_at
    );

  if (
    currentTime === null
  ) {
    return {
      from: '-',
      seconds: null,
    };
  }

  if (
    isUserEvent(current)
  ) {
    let previousUserEvent:
      | StatusObj
      | undefined;

    if (
      isReadEvent(current)
    ) {
      previousUserEvent =
        getLatestBefore(
          rows,
          index,
          isDeliveredEvent
        );
    } else if (
      isCtaEvent(current)
    ) {
      previousUserEvent =
        getLatestBefore(
          rows,
          index,
          isReadEvent
        ) ||
        getLatestBefore(
          rows,
          index,
          isDeliveredEvent
        );
    } else if (
      isChatEvent(current)
    ) {
      previousUserEvent =
        getLatestBefore(
          rows,
          index,
          isDeliveredEvent
        );
    }

    const previousTime =
      toTime(
        previousUserEvent?.updated_at ||
        previousUserEvent?.created_at
      );

    return {
      from:
        getStatusLabel(
          previousUserEvent ||
          {}
        ),
      seconds:
        previousTime === null
          ? null
          : Math.max(
            0,
            (currentTime -
              previousTime) /
            1000
          ),
    };
  }

  if (
    isAgentResponseEvent(
      current
    )
  ) {
    const previousUser =
      getLatestBefore(
        rows,
        index,
        isUserEvent
      );

    const previousTime =
      toTime(
        previousUser?.updated_at ||
        previousUser?.created_at
      );

    return {
      from:
        getStatusLabel(
          previousUser ||
          {}
        ),
      seconds:
        previousTime === null
          ? null
          : Math.max(
            0,
            (currentTime -
              previousTime) /
            1000
          ),
    };
  }

  return {
    from: '-',
    seconds: null,
  };
};

/* =========================================================
   COMPONENT
========================================================= */

const AllUpdateStatusModal:
  React.FC<
    AllUpdateStatusModalProps
  > = ({
    open,
    onClose,
    selectedRow,
    leadFormId,
  }) => {

    const actions = LeadFormActions();

    const templateActions = TemplateActions();

    const {
      showSnackBar,
    } = useSnackBar();

    /* =====================================================
       STATUS STATE
    ===================================================== */

    const [
      statusList,
      setStatusList,
    ] = useState<
      StatusObj[]
    >([]);

    const [
      loading,
      setLoading,
    ] = useState(false);

    const [
      selectedPayloadFields,
      setSelectedPayloadFields,
    ] = useState<
      string[]
    >([]);

    const [
      anchorEl,
      setAnchorEl,
    ] = useState<
      HTMLElement | null
    >(null);

    const [
      statusFilter,
      setStatusFilter,
    ] = useState<
      'all' |
      'system' |
      'user' |
      'agent'
    >('all');

    /* =====================================================
       TEMPLATE STATE
    ===================================================== */

    const [
      selectedTemplate,
      setSelectedTemplate,
    ] = useState<
      SelectedTemplate | null
    >(null);

    const [
      templateLoading,
      setTemplateLoading,
    ] = useState(false);

    const [
      templateDateSummary,
      setTemplateDateSummary,
    ] = useState<
      DateSummary[]
    >([]);

    const [
      selectedTemplateDate,
      setSelectedTemplateDate,
    ] = useState('');

    const [
      templateStartDate,
      setTemplateStartDate,
    ] = useState('');

    const [
      templateEndDate,
      setTemplateEndDate,
    ] = useState('');

    const [
      templateUsers,
      setTemplateUsers,
    ] = useState<any[]>([]);

    const [
      templateUsersTotal,
      setTemplateUsersTotal,
    ] = useState(0);

    const [
      templateUsersLoading,
      setTemplateUsersLoading,
    ] = useState(false);

    const [
      templateTab,
      setTemplateTab,
    ] = useState<
      'details' |
      'dates' |
      'users'
    >('details');

    /* =====================================================
       LEAD INFO
    ===================================================== */

    const leadId =
      selectedRow?.id ||
      selectedRow?._id ||
      leadFormId ||
      '';

    const phoneNumber =
      getFirstValue(
        selectedRow,
        [
          'phone_number',
          'mobile',
          'mobile_number',
          'contact_number',
          'bot_number',
        ]
      );

    const botNumber =
      getFirstValue(
        selectedRow,
        [
          'bot_number',
          'phone_number',
          'mobile',
          'mobile_number',
        ]
      );

    const selectedRowTemplateId =
      getFirstValue(
        selectedRow,
        [
          'template_id',
          'templateId',
          'md_id',
        ]
      );

    const selectedRowTemplateNames =
      getTemplateNamesFromRow(
        selectedRow
      );

    const selectedRowTemplateName =
      selectedRowTemplateNames[0] ||
      getFirstValue(
        selectedRow,
        [
          'templateName',
        ]
      );

    const selectedRowSentDate =
      getFirstValue(
        selectedRow,
        [
          'sent_date',
          'template_sent_date',
          'templateSentDate',
          'created_at',
          'created',
        ]
      );

    /* =====================================================
       FETCH STATUS
    ===================================================== */

    const fetchStatusList =
      async () => {
        if (!leadId) {
          setStatusList([]);
          return;
        }

        try {
          setLoading(true);

          const res: any =
            await actions.fetchLeadStatus1({
              id: leadId,
            });

          const payload =
            getResponse(res);

          const data =
            Array.isArray(payload)
              ? payload
              : Array.isArray(
                payload?.data
              )
                ? payload.data
                : [];

          // const sorted =
          //   [...data].sort(
          //     (a, b) => {
          //       const aTime =
          //         toTime(
          //           a?.updated_at ||
          //           a?.created_at
          //         ) || 0;

          //       const bTime =
          //         toTime(
          //           b?.updated_at ||
          //           b?.created_at
          //         ) || 0;

          //       return (
          //         aTime -
          //         bTime
          //       );
          //     }
          //   );

          // setStatusList(
          //   sorted
          // );
          setStatusList(data);
        } catch (error) {
          console.error(
            'fetchLeadStatus1 error:',
            error
          );

          setStatusList([]);

          showSnackBar(
            'Failed to fetch status logs',
            'error'
          );
        } finally {
          setLoading(false);
        }
      };

    /* =====================================================
       MODAL OPEN
    ===================================================== */

    useEffect(() => {
      if (!open) return;

      setSelectedTemplate(null);
      setTemplateDateSummary([]);
      setSelectedTemplateDate('');
      setTemplateStartDate('');
      setTemplateEndDate('');
      setTemplateUsers([]);
      setTemplateUsersTotal(0);
      setTemplateTab('details');
      setStatusFilter('all');
      setSelectedPayloadFields([]);

      fetchStatusList();
    }, [
      open,
      leadId,
    ]);

    /* =====================================================
       GET TEMPLATE ID USING TemplateActions
       
       Flow:
       
       1. Status row template_id
       2. template array id
       3. TemplateActions API by name
    ===================================================== */

    const resolveTemplateId =
      async (
        row: StatusObj,
        templateName: string
      ): Promise<string> => {

        /*
         STEP 1:
         Try ID directly from status row.
        */

        const directId =
          getTemplateIdFromRow(
            row,
            templateName
          );

        if (directId) {
          console.log(
            'Template ID found in status row:',
            directId
          );

          return directId;
        }

        /*
         STEP 2:
         If ID not available,
         call TemplateActions.
        */

        try {
          console.log(
            'Template ID not found in status row.'
          );

          console.log(
            'Calling TemplateActions for:',
            templateName
          );

          /*
           IMPORTANT:

           If your TemplateActions method has
           different parameter names, keep the
           same method but adjust this object.

           Expected API:
           /whatsapp/template/
           ?bot_number=...
           &name=...
          */

          const response =
            await templateActions.getTemplateByNameAndBotNumber(
              templateName,
              botNumber
            );

          console.log(
            'TemplateActions response:',
            response
          );

          const result =
            getResponse(
              response
            );

          /*
           Handle different API response formats.
          */

          const templateData =
            result?.data ||
            result?.template ||
            result;

          const templateId =
            templateData?.id ||
            templateData?.template_id ||
            templateData?.md_id ||
            templateData?.data?.id ||
            templateData?.data?.template_id ||
            '';

          if (
            templateId
          ) {
            console.log(
              'Template ID resolved from TemplateActions:',
              templateId
            );

            return String(
              templateId
            );
          }

          /*
           Sometimes API returns array.
          */

          if (
            Array.isArray(
              result
            )
          ) {
            const matched =
              result.find(
                (item: any) =>
                  normalize(
                    item?.name ||
                    item?.template_name
                  ) ===
                  normalize(
                    templateName
                  )
              ) ||
              result[0];

            const arrayId =
              matched?.id ||
              matched?.template_id ||
              matched?.md_id ||
              '';

            if (
              arrayId
            ) {
              return String(
                arrayId
              );
            }
          }

          return '';
        } catch (
        error
        ) {
          console.error(
            'TemplateActions error:',
            error
          );

          return '';
        }
      };

    /* =====================================================
       API 1:
       DATE-WISE TEMPLATE SUMMARY
    ===================================================== */

    const fetchTemplateDateSummary = async (
      templateId: string,
      startDate: string = '',
      endDate: string = ''
    ) => {
      if (!templateId) {
        console.error('Template ID missing');

        showSnackBar(
          'Template ID is missing for this template.',
          'warning'
        );

        return [];
      }

      try {
        setTemplateLoading(true);

        /*
         * IMPORTANT:
         * Do NOT call direct URL here.
         *
         * API call will go through:
         * templateActions.getTemplateDateWiseSummary()
         */

        const res = await templateActions.getTemplateDateWiseSummary({
          template_id: templateId,
          start_date: startDate,
          end_date: endDate,
          next_page: 0,
          sort_by: 'sent_date',
          order: 'desc',
          limit: 20,
        });

        console.log(
          'DATE WISE TEMPLATE SUMMARY RESPONSE:',
          res
        );

        const result = getResponse(res);

        const data =
          Array.isArray(result)
            ? result
            : Array.isArray(result?.data)
              ? result.data
              : Array.isArray(result?.results)
                ? result.results
                : [];

        setTemplateDateSummary(data);

        return data;
      } catch (error) {
        console.error(
          'getTemplateDateWiseSummary error:',
          error
        );

        setTemplateDateSummary([]);

        showSnackBar(
          'Failed to fetch template date-wise summary.',
          'error'
        );

        return [];
      } finally {
        setTemplateLoading(false);
      }
    };

    /* =====================================================
       GET SUMMARY DATE
    ===================================================== */

    const getSummaryDate = (
      row: DateSummary
    ): string => {
      return String(
        row?.sent_date ||
        row?.date ||
        row?.created_at ||
        row?.sentDate ||
        ''
      );
    };

    /* =====================================================
       API 2:
       USER-WISE TEMPLATE SUMMARY
    ===================================================== */

    const fetchTemplateUsers = async (
      templateId: string,
      date: string
    ) => {
      if (!templateId || !date) {
        setTemplateUsers([]);
        setTemplateUsersTotal(0);
        return;
      }

      try {
        setTemplateUsersLoading(true);

        let apiDate = date;

        if (date.includes('T')) {
          apiDate = date.split('T')[0];
        } else if (date.includes(' ')) {
          apiDate = date.split(' ')[0];
        }

        const requestData = {
          templateId: String(templateId),
          sentDate: apiDate,
          page: 0,
          search: '',
          status: '',
          assigned_to: '',
          sortField: 'total_users',
          sortOrder: 'desc',
          limit: 10,
          template_status: '',
        };

        console.log(
          'USER SUMMARY REQUEST:',
          requestData
        );

        const res = await APIService.get(
          `/v0/ui/mca-bot/template-user-summary-list/?template_id=${encodeURIComponent(String(templateId))}&date=${encodeURIComponent(apiDate)}&next_page=0&search=&status=&assigned_to=&sort_by=total_users&order=desc&limit=10&template_status=`
        );

        console.log(
          'TEMPLATE USER SUMMARY RESPONSE:',
          res
        );

        const result = getResponse(res);

        const users =
          Array.isArray(result)
            ? result
            : Array.isArray(result?.data)
              ? result.data
              : Array.isArray(result?.data?.data)
                ? result.data.data
                : Array.isArray(result?.users)
                  ? result.users
                  : Array.isArray(result?.data?.users)
                    ? result.data.users
                    : [];

        const total =
          result?.total_count ??
          result?.total_users ??
          result?.data?.total_count ??
          result?.data?.total_users ??
          users.length;

        setTemplateUsers(users);

        setTemplateUsersTotal(
          Number(total) || users.length
        );
      } catch (error) {
        console.error(
          'getTemplateUserSummaryList error:',
          error
        );

        setTemplateUsers([]);
        setTemplateUsersTotal(0);

        showSnackBar(
          'Failed to fetch template users.',
          'error'
        );
      } finally {
        setTemplateUsersLoading(false);
      }
    };

    /* =====================================================
       TEMPLATE CLICK
       
       MAIN FLOW:
       
       Template Name Click
              ↓
       Resolve Template ID
              ↓
       Date-wise Summary API
              ↓
       User clicks a date
              ↓
       User Summary API
              ↓
       Users Tab
    ===================================================== */

    const handleTemplateClick =
      async (
        row: StatusObj,
        templateName: string
      ) => {

        if (
          !templateName
        ) {
          return;
        }

        console.log(
          '================================='
        );

        console.log(
          'TEMPLATE CLICKED:',
          templateName
        );

        console.log(
          'STATUS ROW:',
          row
        );

        /*
         Reset old data
        */

        setSelectedTemplate({
          name:
            templateName,
          id: '',
          data:
            row?.template ||
            row,
          sentDate:
            row?.updated_at ||
            row?.created_at ||
            selectedRowSentDate,
          sourceStatus:
            row,
        });

        setTemplateDateSummary(
          []
        );

        setSelectedTemplateDate(
          ''
        );

        setTemplateStartDate('');
        setTemplateEndDate('');

        setTemplateUsers(
          []
        );

        setTemplateUsersTotal(
          0
        );

        setTemplateTab(
          'dates'
        );

        /*
         ==========================================
         STEP 1:
         RESOLVE TEMPLATE ID
         ==========================================
        */

        const templateId =
          await resolveTemplateId(
            row,
            templateName
          );

        console.log(
          'FINAL TEMPLATE ID:',
          templateId
        );

        if (!templateId) {

          showSnackBar(
            `Template ID not found for "${templateName}".`,
            'warning'
          );

          setSelectedTemplate(
            prev =>
              prev
                ? {
                  ...prev,
                  id: '',
                }
                : prev
          );

          setTemplateTab(
            'details'
          );

          return;
        }

        /*
         Save resolved ID
        */

        setSelectedTemplate(
          prev =>
            prev
              ? {
                ...prev,
                id: templateId,
              }
              : prev
        );

        /*
         ==========================================
         STEP 2:
         DATE-WISE SUMMARY API
         ==========================================
        */

        const dateRows =
          await fetchTemplateDateSummary(
            templateId
          );

        console.log(
          'DATE SUMMARY ROWS:',
          dateRows
        );

        /*
         ==========================================
         STEP 3:
         STAY ON DATE SUMMARY

         IMPORTANT:
         Do NOT call User Summary API here.
         User Summary is called only after the
         user clicks a specific date.
         ==========================================
        */

        setSelectedTemplateDate('');
        setTemplateTab('dates');

        console.log(
          'DATE SUMMARY LOADED. Waiting for date click.'
        );

        console.log(
          '================================='
        );
      };

    /* =====================================================
       DATE CLICK
    ===================================================== */

    const handleDateClick =
      async (
        summary: DateSummary
      ) => {

        const templateId =
          selectedTemplate?.id ||
          getTemplateIdFromRow(
            selectedTemplate?.data,
            selectedTemplate?.name || ''
          );

        if (!templateId) {
          showSnackBar(
            'Template ID is missing. Please select the template again.',
            'warning'
          );
          return;
        }

        const rawDate =
          getSummaryDate(
            summary
          );

        if (!rawDate) {
          showSnackBar(
            'Date not available in summary.',
            'warning'
          );

          return;
        }

        const parsedDate = dayjs(rawDate);

        if (!parsedDate.isValid()) {
          showSnackBar(
            'Invalid summary date.',
            'warning'
          );
          return;
        }

        const date =
          parsedDate.format('YYYY-MM-DD');

        console.log('DATE CLICKED:', {
          templateId: String(templateId),
          date,
        });

        setSelectedTemplateDate(date);

        await fetchTemplateUsers(
          String(templateId),
          date
        );

        setTemplateTab('users');
      };

    /* =====================================================
       BACK
    ===================================================== */

    const handleBackToLogs =
      () => {

        setSelectedTemplate(
          null
        );

        setTemplateDateSummary(
          []
        );

        setSelectedTemplateDate(
          ''
        );

        setTemplateStartDate('');
        setTemplateEndDate('');

        setTemplateUsers(
          []
        );

        setTemplateUsersTotal(
          0
        );

        setTemplateTab(
          'details'
        );
      };

    /* =====================================================
       TEMPLATE STATUS ROWS
    ===================================================== */

    const templateStatusRows =
      useMemo(() => {

        if (
          !selectedTemplate?.name
        ) {
          return [];
        }

        const target =
          normalize(
            selectedTemplate.name
          );

        return statusList.filter(
          row => {

            const names =
              getTemplateNamesFromRow(
                row
              );

            return names.some(
              name =>
                normalize(
                  name
                ) === target
            );
          }
        );

      }, [
        statusList,
        selectedTemplate,
      ]);

    /* =====================================================
       STATUS FILTER
    ===================================================== */

    const statusRowsWithFilter =
      useMemo(() => {

        if (
          statusFilter ===
          'all'
        ) {
          return statusList;
        }

        return statusList.filter(
          row => {

            const type =
              normalize(
                row?.type_of_status
              );

            if (
              statusFilter ===
              'system'
            ) {
              return (
                type === 's' ||
                type === 'system'
              );
            }

            if (
              statusFilter ===
              'user'
            ) {
              return (
                type === 'u' ||
                type === 'user'
              );
            }

            if (
              statusFilter ===
              'agent'
            ) {
              return (
                type === 'a' ||
                type === 'agent'
              );
            }

            return true;
          }
        );

      }, [
        statusList,
        statusFilter,
      ]);

    /* =====================================================
       PAYLOAD KEYS
    ===================================================== */

    const payloadKeys =
      useMemo(() => {

        const keys =
          new Set<string>();

        statusRowsWithFilter.forEach(
          row => {

            Object.keys(
              row?.payload ||
              {}
            ).forEach(
              key =>
                keys.add(key)
            );

          }
        );

        return Array.from(
          keys
        );

      }, [
        statusRowsWithFilter,
      ]);

    /* =====================================================
       STATUS COLUMNS
    ===================================================== */

    const statusColumns =
      useMemo(
        () => [

          {
            accessorKey:
              'index',

            header: '#',

            size: 55,

            enableSorting:
              false,

            Cell: ({
              row,
            }: any) =>
              row.index + 1,
          },

          {
            accessorKey:
              'status_name',

            header:
              'Status',

            size: 180,

            Cell: ({
              row,
            }: any) => {

              const label =
                getStatusLabel(
                  row.original
                );

              return (
                <Chip
                  size="small"
                  label={label}
                  color={getStatusColor(
                    label
                  )}
                  variant="outlined"
                />
              );
            },
          },

          {
            accessorKey:
              'sub_status',

            header:
              'Sub Status',

            size: 150,

            Cell: ({
              row,
            }: any) => {

              const subStatus =
                row.original
                  ?.sub_status ||
                row.original
                  ?.substatus ||
                row.original
                  ?.sub_status_name ||
                '-';

              return (
                <Chip
                  size="small"
                  label={
                    subStatus
                  }
                  variant="outlined"
                />
              );
            },
          },

          {
            accessorKey:
              'status_type',

            header:
              'Status Type',

            size: 120,

            Cell: ({
              row,
            }: any) => (
              <Chip
                size="small"
                label={getTypeLabel(
                  row.original
                )}
                variant="outlined"
              />
            ),
          },

          {
            accessorKey:
              'template_name',

            header:
              'Template',

            size: 240,

            Cell: ({
              row,
            }: any) => {

              const names =
                getTemplateNamesFromRow(
                  row.original
                );

              if (
                names.length ===
                0
              ) {
                return (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    -
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{
                    display:
                      'flex',
                    flexDirection:
                      'column',
                    gap: 0.5,
                  }}
                >
                  {names.map(
                    (
                      name,
                      index
                    ) => {

                      const rowTemplateId =
                        getTemplateIdFromRow(
                          row.original,
                          name
                        );

                      return (
                        <Tooltip
                          key={`${name}-${index}`}
                          title={
                            rowTemplateId
                              ? `Open template summary (${rowTemplateId})`
                              : `Resolve template and open summary`
                          }
                        >
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() =>
                              handleTemplateClick(
                                row.original,
                                name
                              )
                            }
                            sx={{
                              fontWeight: 600,
                              color:
                                '#3e59e3',
                              cursor:
                                'pointer',
                              textAlign:
                                'left',
                              width:
                                'fit-content',
                              maxWidth:
                                225,
                              overflow:
                                'hidden',
                              textOverflow:
                                'ellipsis',
                              whiteSpace:
                                'nowrap',
                            }}
                          >
                            {
                              name
                            }

                            <OpenInNewIcon
                              sx={{
                                fontSize:
                                  13,
                                ml: 0.5,
                                verticalAlign:
                                  'middle',
                              }}
                            />
                          </Link>
                        </Tooltip>
                      );
                    }
                  )}
                </Box>
              );
            },
          },

          {
            accessorKey:
              'created_by',

            header:
              'Triggered By',

            size: 150,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.created_by ||
              row.original
                ?.role ||
              'System',
          },

          {
            accessorKey:
              'updated_at',

            header:
              'Triggered On',

            size: 180,

            Cell: ({
              row,
            }: any) =>
              formatDate(
                row.original
                  ?.updated_at ||
                row.original
                  ?.created_at
              ),
          },

          {
            accessorKey:
              'response_from_previous',

            header:
              'Response From',

            size: 180,

            Cell: ({
              row,
            }: any) => {

              const info =
                getResponseInfo(
                  statusRowsWithFilter,
                  row.index
                );

              return (
                <Typography
                  variant="body2"
                >
                  {
                    info.from
                  }
                </Typography>
              );
            },
          },

          {
            accessorKey:
              'response_time',

            header:
              'Response Time',

            size: 135,

            Cell: ({
              row,
            }: any) => {

              const info =
                getResponseInfo(
                  statusRowsWithFilter,
                  row.index
                );

              return (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={
                    info.seconds !==
                      null
                      ? info.seconds >
                        1800
                        ? 'error.main'
                        : 'success.main'
                      : 'text.secondary'
                  }
                >
                  {formatDuration(
                    info.seconds
                  )}
                </Typography>
              );
            },
          },

          {
            accessorKey:
              'comment',

            header:
              'Comments',

            size: 260,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.comment ||
              '-',
          },

          ...selectedPayloadFields.map(
            key => ({

              accessorKey:
                `payload.${key}`,

              header:
                `Payload: ${key}`,

              size: 160,

              Cell: ({
                row,
              }: any) => {

                const value =
                  row.original
                    ?.payload?.[
                  key
                  ];

                if (
                  value ===
                  null ||
                  value ===
                  undefined
                ) {
                  return '-';
                }

                return typeof value ===
                  'object'
                  ? JSON.stringify(
                    value
                  )
                  : String(
                    value
                  );
              },
            })
          ),
        ],
        [
          statusRowsWithFilter,
          selectedPayloadFields,
        ]
      );

    /* =====================================================
       TEMPLATE USER COLUMNS
    ===================================================== */

    const templateUserColumns =
      useMemo(() => {

        const getUserValue =
          (
            row: any,
            keys: string[]
          ) =>
            getFirstValue(
              row,
              keys
            );

        return [

          {
            accessorKey:
              'index',

            header: '#',

            size: 55,

            enableSorting:
              false,

            Cell: ({
              row,
            }: any) =>
              row.index + 1,
          },

          {
            accessorKey:
              'user_name',

            header:
              'User Name',

            size: 160,

            Cell: ({
              row,
            }: any) =>
              getUserValue(
                row.original,
                [
                  'user_name',
                  'name',
                  'customer_name',
                ]
              ) || '-',
          },

          {
            accessorKey:
              'whatsapp_name',

            header:
              'WhatsApp Name',

            size: 160,

            Cell: ({
              row,
            }: any) =>
              getUserValue(
                row.original,
                [
                  'whatsapp_name',
                  'wa_name',
                ]
              ) || '-',
          },

          {
            accessorKey:
              'phone_number',

            header:
              'Phone Number',

            size: 160,

            Cell: ({
              row,
            }: any) =>
              getUserValue(
                row.original,
                [
                  'phone_number',
                  'mobile',
                  'mobile_number',
                ]
              ) || '-',
          },

          {
            accessorKey:
              'status',

            header:
              'Template Status',

            size: 150,

            Cell: ({
              row,
            }: any) => {

              const status =
                getUserValue(
                  row.original,
                  [
                    'status',
                    'template_status',
                  ]
                ) || '-';

              return (
                <Chip
                  size="small"
                  label={status}
                  color={getStatusColor(
                    status
                  )}
                  variant="outlined"
                />
              );
            },
          },

          {
            accessorKey:
              'updated_at',

            header:
              'Updated At',

            size: 180,

            Cell: ({
              row,
            }: any) =>
              formatDate(
                getUserValue(
                  row.original,
                  [
                    'updated_at',
                    'created_at',
                  ]
                )
              ),
          },

          {
            accessorKey:
              'assigned_to',

            header:
              'Assigned To',

            size: 150,

            Cell: ({
              row,
            }: any) =>
              getUserValue(
                row.original,
                [
                  'assigned_to',
                  'assigned_agent',
                ]
              ) || '-',
          },

          {
            accessorKey:
              'last_call_status',

            header:
              'Agent / Call Status',

            size: 190,

            Cell: ({
              row,
            }: any) =>
              getUserValue(
                row.original,
                [
                  'last_call_status',
                  'call_status',
                  'agent_status',
                ]
              ) || '-',
          },

          {
            accessorKey:
              'comments',

            header:
              'Comments',

            size: 240,

            Cell: ({
              row,
            }: any) => {

              const comments =
                row.original
                  ?.comments;

              if (
                Array.isArray(
                  comments
                )
              ) {
                return (
                  <Typography
                    variant="body2"
                  >
                    {comments[
                      comments.length -
                      1
                    ]?.comment ||
                      '-'}
                  </Typography>
                );
              }

              return (
                <Typography
                  variant="body2"
                >
                  {row.original
                    ?.comment ||
                    '-'}
                </Typography>
              );
            },
          },

        ];
      }, []);

    /* =====================================================
       TEMPLATE DETAIL KEYS
    ===================================================== */

    const templateDetailPayloadKeys =
      useMemo(() => {

        const templateData =
          selectedTemplate
            ?.data || {};

        if (
          !templateData ||
          typeof templateData !==
          'object'
        ) {
          return [];
        }

        return Object.keys(
          templateData
        ).filter(
          key =>
            ![
              'md_id',
              'template_id',
              'id',
              'name',
              'template_name',
            ].includes(key)
        );

      }, [
        selectedTemplate,
      ]);

    /* =====================================================
       DATE SUMMARY COLUMNS
    ===================================================== */

    const templateDateColumns =
      useMemo(
        () => [

          {
            accessorKey:
              'index',

            header: '#',

            size: 55,

            enableSorting:
              false,

            Cell: ({
              row,
            }: any) =>
              row.index + 1,
          },

          {
            accessorKey:
              'sent_date',

            header:
              'Date',

            size: 180,

            Cell: ({
              row,
            }: any) => {

              const date =
                getSummaryDate(
                  row.original
                );

              return (
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    handleDateClick(
                      row.original
                    )
                  }
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'none',
                  }}
                >
                  {formatSummaryDate(
                    date
                  )}
                </Button>
              );
            },
          },

          {
            accessorKey:
              'total_users',

            header:
              'Total Users',

            size: 130,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.total_users ??
              row.original
                ?.total ??
              row.original
                ?.users ??
              '-',
          },

          {
            accessorKey:
              'delivered',

            header:
              'Delivered',

            size: 120,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.delivered ??
              row.original
                ?.delivered_count ??
              '-',
          },

          {
            accessorKey:
              'read',

            header:
              'Read',

            size: 120,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.read ??
              row.original
                ?.read_count ??
              '-',
          },

          {
            accessorKey:
              'clicked',

            header:
              'Clicked',

            size: 120,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.clicked ??
              row.original
                ?.clicked_count ??
              '-',
          },

          {
            accessorKey:
              'failed',

            header:
              'Failed',

            size: 120,

            Cell: ({
              row,
            }: any) =>
              row.original
                ?.failed ??
              row.original
                ?.failed_count ??
              '-',
          },

        ],
        [selectedTemplate?.id, selectedTemplate?.name]
      );

    /* =====================================================
       RENDER STATUS LOGS
    ===================================================== */

    const renderStatusLogs =
      () => (
        <>

          <Box
            sx={{
              px: 3,
              py: 1.25,
              display:
                'flex',
              alignItems:
                'center',
              gap: 1,
              flexWrap:
                'wrap',
              borderBottom:
                '1px solid',
              borderColor:
                'divider',
            }}
          >

            {(
              [
                'all',
                'system',
                'user',
                'agent',
              ] as const
            ).map(
              filter => (
                <Button
                  key={
                    filter
                  }
                  size="small"
                  variant={
                    statusFilter ===
                      filter
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={() =>
                    setStatusFilter(
                      filter
                    )
                  }
                >
                  {filter ===
                    'all'
                    ? 'All'
                    : filter[0].toUpperCase() +
                    filter.slice(
                      1
                    )}
                </Button>
              )
            )}

          </Box>

          <Box
            sx={{
              px: 2.5,
              pb: 2,
              flex: 1,
              minHeight: 0,
            }}
          >

            {loading ? (

              <Box
                sx={{
                  height: 300,
                  display:
                    'flex',
                  alignItems:
                    'center',
                  justifyContent:
                    'center',
                  gap: 1,
                }}
              >
                <CircularProgress
                  size={24}
                />

                <Typography>
                  Loading status logs...
                </Typography>
              </Box>

            ) : (

              <CommonTable
                columns={
                  statusColumns
                }
                data={
                  statusRowsWithFilter
                }
                enableStickyHeader
                enableBottomToolbar={
                  false
                }
                muiTableContainerProps={{
                  sx: {
                    maxHeight:
                      'calc(94vh - 350px)',
                    overflow:
                      'auto',
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    backgroundImage:
                      'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                    color:
                      'white',
                    fontWeight:
                      600,
                    fontSize:
                      '13px',
                  },
                }}
              />

            )}

          </Box>

        </>
      );

    /* =====================================================
       RENDER TEMPLATE DETAILS
    ===================================================== */

    const renderTemplateDetails =
      () => {

        const templateData =
          selectedTemplate
            ?.data || {};

        return (
          <>

            <Box
              sx={{
                px: 3,
                py: 1,
                borderBottom:
                  '1px solid',
                borderColor:
                  'divider',
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 1,
              }}
            >

              <Button
                size="small"
                startIcon={
                  <ArrowBackIcon />
                }
                onClick={
                  handleBackToLogs
                }
              >
                Back to Status Logs
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  ml: 1,
                }}
              >
                /
                {' '}
                {
                  selectedTemplate?.name
                }
              </Typography>

            </Box>

            <Box
              sx={{
                px: 3,
              }}
            >

              <Tabs
                value={
                  templateTab
                }
                onChange={(
                  _,
                  value
                ) => {
                  if (
                    value === 'users' &&
                    !selectedTemplateDate
                  ) {
                    showSnackBar(
                      'Please select a date first.',
                      'warning'
                    );
                    setTemplateTab('dates');
                    return;
                  }

                  setTemplateTab(value);
                }}
                sx={{
                  minHeight:
                    44,
                }}
              >

                <Tab
                  value="details"
                  label="Template Details"
                />

                <Tab
                  value="dates"
                  label={`Date Summary (${templateDateSummary.length})`}
                />

                <Tab
                  value="users"
                  label={`Users (${templateUsersTotal})`}
                />

              </Tabs>

            </Box>

            <Divider />

            <Box
              sx={{
                px: 3,
                py: 2,
                flex: 1,
                minHeight: 0,
                overflow:
                  'auto',
              }}
            >

              {/* =================================================
                  DETAILS
              ================================================= */}

              {templateTab ===
                'details' && (
                  <>

                    <Box
                      sx={{
                        display:
                          'grid',
                        gridTemplateColumns:
                        {
                          xs: '1fr',
                          md: 'repeat(4, 1fr)',
                        },
                        gap: 1.5,
                      }}
                    >

                      <Box
                        sx={{
                          p: 1.5,
                          border:
                            '1px solid',
                          borderColor:
                            'divider',
                          borderRadius:
                            1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Template ID
                        </Typography>

                        <Typography
                          fontWeight={600}
                          sx={{
                            wordBreak:
                              'break-all',
                          }}
                        >
                          {
                            selectedTemplate?.id ||
                            '-'
                          }
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          border:
                            '1px solid',
                          borderColor:
                            'divider',
                          borderRadius:
                            1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Name
                        </Typography>

                        <Typography
                          fontWeight={600}
                        >
                          {
                            selectedTemplate?.name ||
                            '-'
                          }
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          border:
                            '1px solid',
                          borderColor:
                            'divider',
                          borderRadius:
                            1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Status
                        </Typography>

                        <Typography
                          fontWeight={600}
                        >
                          {
                            templateData?.status ||
                            templateData?.template_status ||
                            '-'
                          }
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          border:
                            '1px solid',
                          borderColor:
                            'divider',
                          borderRadius:
                            1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Sent / Created
                        </Typography>

                        <Typography
                          fontWeight={600}
                        >
                          {formatDate(
                            selectedTemplate?.sentDate
                          )}
                        </Typography>
                      </Box>

                      {templateDetailPayloadKeys.map(
                        key => (
                          <Box
                            key={
                              key
                            }
                            sx={{
                              p: 1.5,
                              border:
                                '1px solid',
                              borderColor:
                                'divider',
                              borderRadius:
                                1.5,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                key
                              }
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.25,
                                wordBreak:
                                  'break-word',
                              }}
                            >
                              {typeof templateData[
                                key
                              ] ===
                                'object'
                                ? JSON.stringify(
                                  templateData[
                                  key
                                  ]
                                )
                                : String(
                                  templateData[
                                  key
                                  ] ??
                                  '-'
                                )}
                            </Typography>

                          </Box>
                        )
                      )}

                    </Box>

                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        mt: 3,
                        mb: 1.5,
                      }}
                    >
                      Status History for this Template
                    </Typography>

                    <CommonTable
                      columns={
                        statusColumns
                      }
                      data={
                        templateStatusRows
                      }
                      enableStickyHeader
                      enableBottomToolbar={
                        false
                      }
                      muiTableContainerProps={{
                        sx: {
                          maxHeight:
                            320,
                          overflow:
                            'auto',
                        },
                      }}
                      muiTableHeadCellProps={{
                        sx: {
                          backgroundImage:
                            'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                          color:
                            'white',
                          fontWeight:
                            600,
                          fontSize:
                            '13px',
                        },
                      }}
                    />

                  </>
                )}

              {/* =================================================
                  DATE SUMMARY
              ================================================= */}

              {templateTab ===
                'dates' && (

                  <>
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                      >
                        Filter by Date Range
                      </Typography>

                      <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        value={templateStartDate}
                        onChange={async (e) => {
                          const value = e.target.value;
                          setTemplateStartDate(value);

                          if (value && templateEndDate && value > templateEndDate) {
                            setTemplateEndDate('');
                            return;
                          }

                          if (selectedTemplate?.id) {
                            await fetchTemplateDateSummary(
                              selectedTemplate.id,
                              value,
                              templateEndDate
                            );
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          max: dayjs().format('YYYY-MM-DD'),
                        }}
                        sx={{ width: 150 }}
                      />

                      <TextField
                        label="End Date"
                        type="date"
                        size="small"
                        value={templateEndDate}
                        disabled={!templateStartDate}
                        onChange={async (e) => {
                          const value = e.target.value;
                          setTemplateEndDate(value);

                          if (!value || !selectedTemplate?.id) return;

                          await fetchTemplateDateSummary(
                            selectedTemplate.id,
                            templateStartDate,
                            value
                          );
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: templateStartDate || undefined,
                          max: dayjs().format('YYYY-MM-DD'),
                        }}
                        sx={{ width: 150 }}
                      />

                      {(templateStartDate || templateEndDate) && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            setTemplateStartDate('');
                            setTemplateEndDate('');

                            if (selectedTemplate?.id) {
                              await fetchTemplateDateSummary(
                                selectedTemplate.id,
                                '',
                                ''
                              );
                            }
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>

                    {templateLoading ? (

                      <Box
                        sx={{
                          minHeight:
                            300,
                          display:
                            'flex',
                          alignItems:
                            'center',
                          justifyContent:
                            'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress />

                        <Typography>
                          Loading date-wise template summary...
                        </Typography>
                      </Box>

                    ) : templateDateSummary.length ===
                      0 ? (

                      <Box
                        sx={{
                          minHeight:
                            250,
                          display:
                            'flex',
                          alignItems:
                            'center',
                          justifyContent:
                            'center',
                        }}
                      >
                        <Typography
                          color="text.secondary"
                        >
                          No date-wise summary found.
                        </Typography>
                      </Box>

                    ) : (

                      <CommonTable
                        columns={
                          templateDateColumns
                        }
                        data={
                          templateDateSummary
                        }
                        enableStickyHeader
                        enableBottomToolbar={
                          false
                        }
                        muiTableContainerProps={{
                          sx: {
                            maxHeight:
                              'calc(90vh - 330px)',
                            overflow:
                              'auto',
                          },
                        }}
                        muiTableHeadCellProps={{
                          sx: {
                            backgroundImage:
                              'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                            color:
                              'white',
                            fontWeight:
                              600,
                            fontSize:
                              '13px',
                          },
                        }}
                      />

                    )}
                  </>

                )}

              {/* =================================================
                  USERS
              ================================================= */}

              {templateTab ===
                'users' && (

                  <>

                    <Box
                      sx={{
                        mb: 2,
                        display:
                          'flex',
                        alignItems:
                          'center',
                        justifyContent:
                          'space-between',
                        gap: 2,
                        flexWrap:
                          'wrap',
                      }}
                    >

                      <Box>

                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                        >
                          Template Users
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Date:{' '}
                          {selectedTemplateDate
                            ? formatDate(
                              selectedTemplateDate
                            )
                            : '-'}
                        </Typography>

                      </Box>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setTemplateTab(
                            'dates'
                          )
                        }
                      >
                        Back to Date Summary
                      </Button>

                    </Box>

                    {templateUsersLoading ? (

                      <Box
                        sx={{
                          minHeight:
                            300,
                          display:
                            'flex',
                          alignItems:
                            'center',
                          justifyContent:
                            'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress />

                        <Typography>
                          Loading template users...
                        </Typography>
                      </Box>

                    ) : (

                      <CommonTable
                        columns={
                          templateUserColumns
                        }
                        data={
                          templateUsers
                        }
                        enableStickyHeader
                        enableBottomToolbar={
                          false
                        }
                        muiTableContainerProps={{
                          sx: {
                            maxHeight:
                              'calc(90vh - 350px)',
                            overflow:
                              'auto',
                          },
                        }}
                        muiTableHeadCellProps={{
                          sx: {
                            backgroundImage:
                              'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                            color:
                              'white',
                            fontWeight:
                              600,
                            fontSize:
                              '13px',
                          },
                        }}
                      />

                    )}

                  </>
                )}

            </Box>

            <Box
              sx={{
                px: 3,
                py: 1.25,
                borderTop:
                  '1px solid',
                borderColor:
                  'divider',
                display:
                  'flex',
                justifyContent:
                  'space-between',
                alignItems:
                  'center',
              }}
            >

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {templateTab ===
                  'users'
                  ? `Showing ${templateUsers.length} of ${templateUsersTotal} users`
                  : templateTab ===
                    'dates'
                    ? `Showing ${templateDateSummary.length} date-wise records`
                    : `Showing ${templateStatusRows.length} status records`}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                onClick={
                  handleBackToLogs
                }
              >
                Back
              </Button>

            </Box>

          </>
        );
      };

    /* =====================================================
       MAIN RENDER
    ===================================================== */

    return (
      <Modal
        open={open}
        onClose={onClose}
      >

        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',

            width: {
              xs: '96%',
              md: '94%',
              lg: '92%',
            },

            maxWidth:
              1550,

            height: {
              xs: '94vh',
              md: '90vh',
            },

            bgcolor:
              'background.paper',

            borderRadius:
              2,

            boxShadow: 24,

            overflow:
              'hidden',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >

          {/* =================================================
              HEADER
          ================================================= */}

          <Box
            sx={{
              px: 3,
              pt: 2.25,
              pb: 1.5,
              borderBottom:
                '1px solid',
              borderColor:
                'divider',
            }}
          >

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={2}
            >

              <Box>

                <Typography
                  variant="h6"
                  fontWeight={700}
                >
                  {selectedTemplate
                    ? 'Template Details'
                    : 'Status Logs'}
                </Typography>

                {/* <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {phoneNumber ||
                    'Lead'}

                  {selectedTemplate
                    ? ` • ${selectedTemplate.name}`
                    : ` • ${statusList.length} status records`}
                </Typography> */}

              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
              >

                {!selectedTemplate && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={e =>
                      setAnchorEl(
                        e.currentTarget
                      )
                    }
                    disabled={
                      payloadKeys.length ===
                      0
                    }
                  >
                    Select Payload Columns
                  </Button>
                )}

                <IconButton
                  onClick={
                    onClose
                  }
                >
                  <CloseIcon />
                </IconButton>

              </Box>

            </Box>

            {!selectedTemplate && (

              <Box
                sx={{
                  mt: 1.75,
                  p: 1.5,
                  border:
                    '1px solid',
                  borderColor:
                    'divider',
                  borderRadius:
                    1.5,

                  display:
                    'grid',

                  gridTemplateColumns:
                  {
                    xs: '1fr 1fr',
                    md: 'repeat(4, 1fr)',
                  },

                  gap: 2,
                }}
              >

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Lead ID
                  </Typography>

                  <Typography
                    fontWeight={600}
                  >
                    {leadId ||
                      '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Phone Number
                  </Typography>

                  <Typography
                    fontWeight={600}
                  >
                    {phoneNumber ||
                      '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Current Template
                  </Typography>

                  <Typography
                    fontWeight={600}
                  >
                    {selectedRowTemplateName ||
                      '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Template ID
                  </Typography>

                  <Typography
                    fontWeight={600}
                    sx={{
                      wordBreak:
                        'break-all',
                    }}
                  >
                    {selectedRowTemplateId ||
                      '-'}
                  </Typography>
                </Box>

              </Box>

            )}

          </Box>

          {/* =================================================
              CONTENT
          ================================================= */}

          {selectedTemplate
            ? renderTemplateDetails()
            : renderStatusLogs()}

          {/* =================================================
              PAYLOAD POPOVER
          ================================================= */}

          <Popover
            open={Boolean(
              anchorEl
            )}
            anchorEl={
              anchorEl
            }
            onClose={() =>
              setAnchorEl(
                null
              )
            }
            anchorOrigin={{
              vertical:
                'bottom',
              horizontal:
                'right',
            }}
            transformOrigin={{
              vertical:
                'top',
              horizontal:
                'right',
            }}
          >

            <Box
              sx={{
                p: 2,
                minWidth: 250,
                maxWidth: 360,
                maxHeight: 320,
                overflow:
                  'auto',
              }}
            >

              {payloadKeys.length ===
                0 ? (

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No payload keys found
                </Typography>

              ) : (

                <FormGroup>

                  {payloadKeys.map(
                    field => (

                      <FormControlLabel
                        key={
                          field
                        }
                        control={
                          <Checkbox
                            checked={selectedPayloadFields.includes(
                              field
                            )}
                            onChange={() =>
                              setSelectedPayloadFields(
                                prev =>
                                  prev.includes(
                                    field
                                  )
                                    ? prev.filter(
                                      x =>
                                        x !==
                                        field
                                    )
                                    : [
                                      ...prev,
                                      field,
                                    ]
                              )
                            }
                          />
                        }
                        label={
                          field
                        }
                      />

                    )
                  )}

                </FormGroup>

              )}

            </Box>

          </Popover>

        </Box>

      </Modal>
    );
  };

export default AllUpdateStatusModal;